<script>
import PdfUploader from "@/components/trainings/PdfUploader.vue";
import VueTagsInput from '@sipec/vue3-tags-input';
import ImageSlide from "@/components/trainings/form/ImageSlide.vue";
import IconUploader from "@/components/trainings/form/IconUploader.vue";
import TrainingCategorySelect from "@/components/trainings/form/TrainingCategorySelect.vue";
import AudioUploader from "@/components/trainings/form/AudioUploader.vue";
import MultiFileSelect from "@/components/trainings/form/MultiFileSelect.vue";
import DatePicker from "@/components/form/DatePicker.vue";
import { format } from 'date-fns';
import Tooltip from "@/components/ui/Tooltip.vue";
import { SUPPORTED_LOCALES } from "@/utils/locale.js";

export default {
    components: {
        Tooltip,
        DatePicker,
        MultiFileSelect,
        AudioUploader,
        TrainingCategorySelect,
        IconUploader,
        ImageSlide,
        PdfUploader,
        VueTagsInput
    },
    data() {
        return {
            error: '',
            errors: {},
            loading: false,
            loadingCategories: true,
            controller: {
                data: new AbortController(),
                tags: new AbortController(),
            },
            audioFile: null,
            audioRemoved: false,
            audioDuration: 0,
            categories: [],
            tags: [],
            tag: '',
            iconImageFile: null,
            iconImagePreview: null,
            iconImageRemoved: false,
        }
    },
    props: ['training'],
    methods: {
        submit() {
            this.loading = true;

            const formData = new FormData();
            formData.append('name', this.training.name);
            formData.append('is_active', this.training.is_active);
            formData.append('price', this.training.price);
            formData.append('description', this.training.description);
            formData.append('meeting_url', this.training.meeting_url || '');
            formData.append('language', this.training.language || '');
            formData.append('is_available_individually', this.training.is_available_individually);
            if (this.training.training_category_id) {
                formData.append('training_category_id', this.training.training_category_id);
            }
            if (this.iconImageFile) {
                formData.append('icon_image', this.iconImageFile);
            } else if (this.iconImageRemoved) {
                formData.append('icon_image_removed', '1');
            }

            this.training.dates.forEach((date, index) => {
                if (date.id) {
                    formData.append(`dates[${index}][id]`, date.id);
                }
                if (date.starts_at) {
                    const formattedDate = format(date.starts_at, 'yyyy-MM-dd HH:mm');
                    formData.append(`dates[${index}][starts_at]`, formattedDate);
                }
            });

            this.training.tags.map(tag => tag.text).forEach(tagText => {
                formData.append('tags[]', tagText);
            });

            this.training.images.forEach((image, index) => {
                if (image.file) {
                    formData.append(`images[${index}][file]`, image.file);
                    formData.append(`images[${index}][timestamp]`, image.timestamp || '');
                }
                if (image.id) {
                    formData.append(`images[${index}][id]`, image.id);
                    formData.append(`images[${index}][timestamp]`, image.timestamp || '');
                }
            });

            if (this.audioFile) {
                formData.append('audio', this.audioFile);
            } else if (this.audioRemoved) {
                formData.append('audio_removed', '1');
            }

            let url = '/trainings';
            if (this.training.id) {
                url += '/' + this.training.id;
            }

            axios.post(url, formData, {
                signal: this.controller.data.signal,
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            }).then(() => {
                this.$toast.success(this.$t('trainings.messages.training_saved'));
                this.$router.push({ name: 'trainings.index' });
            }).catch((data) => {
                this.errors = data.response?.data?.errors || {};
            }).finally(() => {
                this.loading = false;
            })
        },

        addDate() {
            this.training.dates.push({ id: null, starts_at: null });
        },

        removeDate(index) {
            this.training.dates.splice(index, 1);
        },

        removeIconImage() {
            this.iconImageFile = null;
            this.iconImageRemoved = true;
            if (this.iconImagePreview) {
                URL.revokeObjectURL(this.iconImagePreview);
                this.iconImagePreview = null;
            }
            this.training.icon_image_url = null;
        },

        onIconImageChange(event) {
            const file = event.target.files[0];
            if (file) {
                this.iconImageFile = file;
                this.iconImagePreview = URL.createObjectURL(file);
                this.iconImageRemoved = false;
            }
        },

        addImage() {
            this.training.images.push({
                file: null,
                preview: null,
                timestamp: '',
                id: null
            })
        },

        addMultipleImages(event) {
            const files = Array.from(event.target.files);

            files.forEach(file => {
                this.training.images.push({
                    file: file,
                    preview: URL.createObjectURL(file),
                    timestamp: '',
                    id: null
                });
            });

            if (this.$refs.multipleImagesInput) {
                this.$refs.multipleImagesInput.value = '';
            }
        },

        onPdfImagesProcessed(processedImages) {
            this.training.images.push(...processedImages);
        },

        removeImage(index) {
            const image = this.training.images[index];
            if (image.preview && image.preview.startsWith('blob:')) {
                URL.revokeObjectURL(image.preview);
            }
            this.training.images.splice(index, 1);
        },

        onSlideFileChange(event, image) {
            const file = event.target.files[0];
            if (file) {
                if (image.preview && image.preview.startsWith('blob:')) {
                    URL.revokeObjectURL(image.preview);
                }

                image.file = file;
                image.id = null;
                image.preview = URL.createObjectURL(file);
            } else {
                image.file = null;
                image.preview = null;
            }
        },

        onAudioChange(file) {
            this.audioFile = file;
            if (file) {
                this.audioRemoved = false;
                this.training.audio_url = URL.createObjectURL(file);
            } else {
                this.training.audio_url = null;
            }
        },

        onAudioRemove() {
            this.audioRemoved = true;
            this.training.audio_url = null;
        },

        onAudioDurationChange(duration) {
          this.audioDuration = duration;
        },

        formatTime(seconds) {
            const mins = Math.floor(seconds / 60);
            const secs = Math.floor(seconds % 60);
            return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
        },

        setTimestampsAutomatically() {
            const imagesCount = this.training.images?.length || 0;

            if (!this.training.audio_url) {
                this.$toast?.error(this.$t('trainings.messages.select_audio_first'));
                return;
            }

            if (!imagesCount) {
                this.$toast?.error(this.$t('trainings.messages.add_slides_first'));
                return;
            }

            if (this.audioDuration > 0) {
                this.applyTimestamps(this.audioDuration);
                return;
            }

            const audio = new Audio(this.training.audio_url);
            audio.addEventListener('loadedmetadata', () => {
                const duration = audio.duration || 0;
                if (duration > 0) {
                    this.audioDuration = duration;
                    this.applyTimestamps(duration);
                } else {
                    this.$toast?.error(this.$t('trainings.messages.audio_length_error'));
                }
            });
            audio.addEventListener('error', () => {
                this.$toast?.error(this.$t('trainings.messages.audio_load_error'));
            });
        },

        applyTimestamps(duration) {
            const imagesCount = this.training.images.length;
            const step = duration / imagesCount;

            this.training.images.forEach((image, index) => {
                const timeInSeconds = Math.floor(index * step);
                image.timestamp = this.formatTime(timeInSeconds);
            });

            this.$toast?.success(this.$t('trainings.messages.timestamps_set'));
        },

        onDelete() {
            this.$router.back();
        },

        loadTags() {
            this.controller.tags = new AbortController();
            axios.get('/training-tags', {
                signal: this.controller.tags.signal,
            }).then(response => {
                this.tags = response.data.data.map(function(tag){ return {'text': tag.name}});
            }).catch(() => {
            }).finally(() => {
            });
        }
    },

    computed: {
        filteredTags() {
            return this.tags.filter(i => {
                return i.text.toLowerCase().indexOf(this.tag.toLowerCase()) !== -1;
            });
        },
        languageOptions() {
            return SUPPORTED_LOCALES.map(code => ({
                value: code,
                label: this.$t(`languages.${code}`)
            }));
        }
    },

    mounted() {
        this.loadTags();

        const input = this.$refs.tagsComponent.$el.querySelector('.ti-input')
        if (input) {
            input.classList.add('input')
        }
    },

    beforeUnmount() {
        this.controller.data && this.controller.data.abort();
        this.controller.tags && this.controller.tags.abort();

        this.training.images.forEach(img => {
            if (img.preview && img.preview.startsWith('blob:')) {
                URL.revokeObjectURL(img.preview);
            }
        });

        if (this.iconImagePreview) {
            URL.revokeObjectURL(this.iconImagePreview);
        }
    }
}
</script>

<template>
    <div class="training-form">
        <form @submit.prevent="submit">

            <b-field :label="$t('trainings.fields.name.name')" horizontal custom-class="mb-3"
                     :type="errors.name ? 'is-danger' : null"
                     :message="errors.name ? errors.name[0] : null">
                <b-input required v-model="training.name" icon="school"></b-input>
            </b-field>

            <b-field :label="$t('trainings.fields.is_active.name')" label-for="is_active" class="checkbox-wrapper mb-4" horizontal>
                <b-checkbox v-model="training.is_active"
                            input-id="is_active"
                            true-value="1"
                            false-value="0">
                </b-checkbox>
            </b-field>

            <b-field class="checkbox-wrapper mb-4" horizontal label-for="is_available_individually">
                <template #label>

                    <Tooltip :text="$t('trainings.fields.is_available_individually.info')">
                        <b-icon size="is-small" icon="help-circle-outline"/>
                    </Tooltip>

                    {{ $t('trainings.fields.is_available_individually.name') }}
                </template>
                <b-checkbox v-model="training.is_available_individually"
                            input-id="is_available_individually"
                            true-value="1"
                            false-value="0">
                </b-checkbox>
            </b-field>

            <b-field :label="$t('trainings.fields.price.name')" horizontal custom-class="mb-3"
                     :type="errors.price ? 'is-danger' : null"
                     :message="errors.price ? errors.price[0] : null">
                <b-input required v-model="training.price" icon="cash"></b-input>
            </b-field>

            <TrainingCategorySelect
                v-model="training.training_category_id"
                :errors="errors.training_category_id"
                />

            <b-field :label="$t('trainings.fields.tags.name')" horizontal custom-class="mb-3"
                     :type="errors.tags ? 'is-danger' : null"
                     :message="errors.tags ? errors.tags[0] : null">
                <vue-tags-input
                    ref="tagsComponent"
                    v-model="tag"
                    :tags="training.tags"
                    :placeholder="$t('trainings.fields.tags.placeholder')"
                    :autocomplete-items="filteredTags"
                    @tags-changed="newTags => training.tags = newTags"
                    :add-on-key="[188, 13]"
                />
            </b-field>

            <IconUploader
                :errors="errors.icon_image"
                :iconImageFile="iconImageFile"
                :iconImagePreview="iconImagePreview || training.icon_image_url"
                @onIconImageChange="onIconImageChange"
                @onRemoveIconImage="removeIconImage"
                />

            <b-field :label="$t('trainings.fields.description.name')" horizontal custom-class="mb-3"
                     :type="errors.description ? 'is-danger' : null"
                     :message="errors.description ? errors.description[0] : null">
                <b-input required maxlength="65535" type="textarea" v-model="training.description"></b-input>
            </b-field>

            <b-field :label="$t('trainings.fields.meeting_url.name')" horizontal custom-class="mb-3"
                     :type="errors.meeting_url ? 'is-danger' : null"
                     :message="errors.meeting_url ? errors.meeting_url[0] : null">
                <b-input v-model="training.meeting_url" icon="video-account" :placeholder="$t('trainings.fields.meeting_url.placeholder')"/>
            </b-field>

            <b-field :label="$t('trainings.fields.language.name')" horizontal custom-class="mb-3"
                     :type="errors.language ? 'is-danger' : null"
                     :message="errors.language ? errors.language[0] : null">
                <b-select v-model="training.language" expanded>
                    <option v-for="opt in languageOptions" :key="opt.value" :value="opt.value">
                        {{ opt.label }}
                    </option>
                </b-select>
            </b-field>

            <b-field :label="$t('trainings.fields.dates.name')" horizontal custom-class="mb-3"
                     :type="errors.dates ? 'is-danger' : null"
                     :message="errors.dates ? errors.dates[0] : null">
                <div class="is-fullwidth">
                    <div
                        v-for="(date, index) in training.dates"
                        :key="index"
                        class="is-flex is-align-items-center mb-2"
                        style="gap: 0.75rem;"
                    >
                        <DatePicker
                            v-model="date.starts_at"
                            :placeholder="$t('date_picker.select_date_and_time')"
                            :id="'date_' + index"
                            :minDate="new Date()"
                            format="dd.MM.yyyy HH:mm"
                            :enableTimePicker="true"
                        />
                        <button type="button" class="button is-danger is-small" @click="removeDate(index)">
                            <b-icon icon="delete" size="is-small"></b-icon>
                        </button>
                    </div>

                    <b-button type="is-info is-small" class="mt-2" @click="addDate" icon-left="plus">
                        {{ $t('trainings.labels.add_date') }}
                    </b-button>
                </div>
            </b-field>

            <AudioUploader :errors="errors.audio"
                           :audioUrl="training.audio_url"
                           @onAudioDurationChange="onAudioDurationChange"
                           @onAudioChange="onAudioChange"
                           @onAudioRemove="onAudioRemove"/>

            <b-field :label="$t('trainings.labels.slides')" horizontal custom-class="mb-3">
                <div class="is-fullwidth">

                    <MultiFileSelect @onChange="addMultipleImages"/>

                    <pdf-uploader
                        class="mb-4"
                        @images-processed="onPdfImagesProcessed"
                    />

                    <div class="buttons mb-4">
                        <button type="button" class="button is-warning" @click="setTimestampsAutomatically"
                                :disabled="!training.audio_url || !training.images.length">
                            <b-icon icon="clock" size="is-small"></b-icon>
                            <span>{{ $t('trainings.labels.set_auto_timestamps') }}</span>
                        </button>

                        <button type="button" class="button is-info" @click="addImage">
                            <b-icon icon="plus" size="is-small"></b-icon>
                            <span>{{ $t('trainings.labels.add_slide') }}</span>
                        </button>
                    </div>

                    <ImageSlide
                        v-for="(image, index) in training.images"
                        :key="index"
                        :image="image"
                        @onFileChange="onSlideFileChange($event, image)"
                        @onRemove="removeImage(index)"
                    />
                </div>
            </b-field>

            <section class="buttons py-4 px-6">
                <b-button type="is-primary"
                          native-type="submit"
                          :loading="loading">
                    {{ $t('actions.save') }}
                </b-button>

                <delete-with-confirmation v-if="training.id"
                                          v-on:deleted="onDelete"
                                          :content="$t('trainings.messages.delete_training_confirmation')"
                                          :button-title="$t('trainings.labels.delete_training')"
                                          :url="'/trainings/' + $route.params.id"/>

                <b-button @click="$router.back()">
                    {{ $t('actions.cancel') }}
                </b-button>
            </section>
        </form>
    </div>
</template>
<style scoped>
    .buttons {
        position: sticky;
        bottom: 0;
        z-index: 10;
        background-color: var(--bulma-scheme-main);
        margin-inline: -15px;
    }
</style>
